import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Redirect } from 'react-router-dom';
import FatalFailureScreen from './Components/FatalFailureScreen';
import isWaitingOnAsyncChecks from './functions/isWaitingOnAsyncChecks';
import HardwareChecks from './HardwareChecks/HardwareChecks';
import NetworkPingChecks from './NetworkPingChecks/NetworkPingCheck';
import NetworkSpeedCheck from './NetworkSpeedChecks/NetworkSpeedCheck';
import StudentASRChecks from './StudentASRChecks/StudentASRChecks';
import { PRE_CHECK_MACHINE_STATES, READY_MODULE_TEST_MODES } from './constants'
import { endStudentPreChecks, preCheckItemErroredAndMoveOn, selectStudentPreChecks, startStudentPreChecks, updateSettings } from './studentPreChecksSlice';
import StudentVolumeChecks from './StudentVolumeChecks/StudentVolumeChecks';
import StudentPreCheckErrorBoundary from './Components/StudentPreCheckErrorBoundary'
import { getUserData } from '../../services/API'
import './StudentPreChecks.scss'
import VirtualScreen from '../Reader/VirtualScreen/VirtualScreen';
import CheckWrapper from './Components/CheckWrapper'
import { LOCAL_STORAGE_FLAGS } from './constants';
import StudentInformationService from '../../services/StudentInformationService';
import useStudentPreChecks from './useStudentPreChecks';
import shouldSkipReadyModule from './functions/shouldSkipReadyModule'
import useDocumentTitle from "A11y/hooks/useDocumentTitle";
import { GetBrandedAppName } from '../../services/BrandingService';

function StudentPreChecks(props){
    const {history, initialUserInteraction, speechProps, onPreCheckDone, readyModuleTestMode, forcedAssessment} = props

    const dispatch = useDispatch()

    const [micIcon, setMicIcon] = useState(false);

    const studentPreChecks = useSelector(selectStudentPreChecks);
    const studentPreCheck = useStudentPreChecks({ itemToCheck: studentPreChecks.currentCheck });
    const userData = useMemo(() => getUserData(), []) //QUESTION: is this reliable that it the data is loaded at this point?

    useDocumentTitle(`Student Pre-Checks - ${GetBrandedAppName()}`);

    useEffect(() => {
        const { tutorInSpanish } = userData

        const shouldGoThroughFTUE = !userData.hasSeenFTUE
        const shouldSkipFTUEIntro = !!localStorage.getItem(LOCAL_STORAGE_FLAGS.READY_MODULE_SKIP_FTUE_INTRO) // this is set true when a manual mic permission fix is done.
        const shouldDoAudioChecks = !!forcedAssessment // audio checks are Volume and ASR checks
        const isFullyEnabled = userData.enablePretest && !tutorInSpanish // feature gates the rest of the module, and prevent the full ready module from running in Spanish mode.
        const { sessionStartTime, userId, sessionId, districtId, schoolId, grade, browser } = userData
        const settings = {
            studentId: userId,
            isFullyEnabled,
            shouldGoThroughFTUE: isFullyEnabled && shouldGoThroughFTUE,
            shouldDoAudioChecks: isFullyEnabled && shouldDoAudioChecks,
            shouldSkipFTUEIntro,
            sessionData: {
                userId,
                sessionId,
                sessionStartTime,
                districtId,
                schoolId,
                grade,
                browser,
            },
        }

        if(readyModuleTestMode){
            if(shouldSkipReadyModule(readyModuleTestMode)){
                dispatch(endStudentPreChecks())
            }else{
                window.history.replaceState(null, null, `?readyModuleTestMode=${readyModuleTestMode}`);
                settings.shouldGoThroughFTUE = readyModuleTestMode === READY_MODULE_TEST_MODES.FTUE
                settings.shouldDoAudioChecks = settings.shouldGoThroughFTUE || readyModuleTestMode === READY_MODULE_TEST_MODES.RETURNED_USER_WITH_ASSESSMENTS
                console.log("Ready module settings test mode", settings)
            }
        }

        console.log("Ready module settings", settings)

        if(!studentPreChecks.studentPreChecksStarted){
            // restore to normal path for next user
            localStorage.removeItem(LOCAL_STORAGE_FLAGS.READY_MODULE_SKIP_FTUE_INTRO)
            dispatch(startStudentPreChecks({settings}))
        }else{
            dispatch(updateSettings({settings}))
        }

    }, [readyModuleTestMode])

    useEffect(()=>{
        if(!initialUserInteraction) history.push('/student')
    }, [])

    useEffect(()=>{
        if(shouldSkipReadyModule(readyModuleTestMode)){
            onPreCheckDone()
        }else if(studentPreChecks.studentPreChecksEnded){
            // if the user was going through FTUE and all checks ended, mark them as so.
            if(studentPreChecks.settings.shouldGoThroughFTUE){
                console.log("Mark Seen FTUE", userData.userId)
                StudentInformationService.setFTUESeen(userData.userId, true)
            }
            // tell App state that it is done.
            // doing this to avoid introducing redux to App component for now.
            onPreCheckDone()
        }
    }, [studentPreChecks.studentPreChecksEnded])

    const isWaitingOnAsyncChecksToFinish = isWaitingOnAsyncChecks(studentPreChecks)

    const continueToNextCheckWithError = (error) => {
        dispatch(preCheckItemErroredAndMoveOn({item: studentPreChecks.currentCheck, error: error}))
    }

    /**
     * rendering
     */

    if(studentPreChecks.studentPreCheckFatalExit){
        return <FatalFailureScreen/>
    }

    if(studentPreChecks.studentPreChecksEnded && !studentPreChecks.studentPreCheckFatalExit){
        return <Redirect to="/student"/>
    }

    if(!speechProps || !speechProps.speak){
        return <div className='invisible'>Loading Speech...</div>
    }

    if(!userData){
        return <div className='invisible'>Loading user data...</div>
    }

    const showMicIcon = () => {
        setMicIcon(true);
    }

    return <div className='student-pre-checks'>
        {isWaitingOnAsyncChecksToFinish && <div>Waiting for some background checks to finish.</div>}
        <StudentPreCheckErrorBoundary logEvent={studentPreCheck.logEvent} continueToNextCheckWithError={continueToNextCheckWithError}>
            <VirtualScreen listeningStatusEnabled={micIcon} invisible={props.screenInvisible}>
                <CheckWrapper>
                    {studentPreChecks.currentCheck === PRE_CHECK_MACHINE_STATES.networkSpeedChecks && <NetworkSpeedCheck speechProps={speechProps} toggleReaderContainer={props.toggleReaderContainer}/>}
                    {studentPreChecks.currentCheck === PRE_CHECK_MACHINE_STATES.networkPingChecks && <NetworkPingChecks speechProps={speechProps} toggleReaderContainer={props.toggleReaderContainer}/>}
                    {studentPreChecks.currentCheck === PRE_CHECK_MACHINE_STATES.hardwareChecks && <HardwareChecks speechProps={speechProps} toggleReaderContainer={props.toggleReaderContainer}/>}
                    {studentPreChecks.currentCheck === PRE_CHECK_MACHINE_STATES.studentSpeechVolumeChecks && <StudentVolumeChecks speechProps={speechProps} showMicIcon={showMicIcon} toggleReaderContainer={props.toggleReaderContainer}/>}
                    {studentPreChecks.currentCheck === PRE_CHECK_MACHINE_STATES.studentASRChecks && <StudentASRChecks history={history} speechProps={speechProps} showMicIcon={showMicIcon} toggleReaderContainer={props.toggleReaderContainer} readerContainerVisible={!props.screenInvisible}/>}
                </CheckWrapper>
            </VirtualScreen>
        </StudentPreCheckErrorBoundary>
    </div>
}
export default React.memo(StudentPreChecks)